import React, { useEffect, useMemo, useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Checkbox, TextField, Paper, Typography, Box, IconButton
} from '@mui/material'; // Material-UI imports
import FilterListIcon from '@mui/icons-material/FilterList';
import { User, UserProject } from '../types/userTypes';  // Assuming you have this types.ts file
import { Project } from '../types/projectTypes';
import {  ProjectsToUser, deleteUserProjects } from '../api/userApi';
import ColumnFilterMenu from './ColumnFilterMenu';


// Define props for UserAssignmentTable component
interface UserAssignmentTableProps {
    users: User[];
    project: Project;
  }
  
  const UserAssignmentTable: React.FC<UserAssignmentTableProps> = ({ users, project }) => {
    const [selectedUsers, setSelectedUsers] = useState<UserProject[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const PROFILE_NAMES: { [key: number]: string } = {
        1: 'Director de Proyectos',
        2: 'Control de Obra',
        3: 'Gerente',
        4: 'Visualizador',
        // Add other profiles as needed
      };
  
    // Initialize selectedUsers based on users already assigned to the project
    useEffect(() => {
      if(project?.id){
        const initiallySelectedUsers = users
        .filter(user => user.projects && user.projects.includes(project.id || 1))
        .map(user => ({
          idUserProject: selectedUsers.length + 1,
          identificationNum: user.identificationNum,
          project
        }));
  
        setSelectedUsers(initiallySelectedUsers);
      }
      
    }, [users, project]);
  
    // Function to save the selected user to the API
    const saveUserProjectToAPI = async (userProject: UserProject) => {
      try {
        await ProjectsToUser(userProject);
       
      } catch (error) {
        console.error('Error while saving user assignment:', error);
      }
    };

    const deleteUserProjectToAPI = async (userProject: UserProject) => {
        try {
            await deleteUserProjects(userProject.identificationNum,[]);
         
        } catch (error) {
          console.error('Error while saving user assignment:', error);
        }
      };
  
    // Handle checkbox change
  const handleCheckboxChange = (user: User) => {
    const isSelected = selectedUsers.find((up) => up.identificationNum === user.identificationNum);
    
    if (isSelected) {
      // If the user is already selected, remove them
      const updatedSelectedUsers = selectedUsers.filter((up) => up.identificationNum !== user.identificationNum);
      setSelectedUsers(updatedSelectedUsers);
      
      // Call the API to delete this user assignment
      const userProjectToDelete: UserProject = {
        idUserProject: isSelected.idUserProject,
        identificationNum: user.identificationNum,
        project
      };
      deleteUserProjectToAPI(userProjectToDelete);
    } else {
      // If the user is not selected, add them
      const newUserProject: UserProject = {
        idUserProject: selectedUsers.length + 1,
        identificationNum: user.identificationNum,
        project
      };

      setSelectedUsers([...selectedUsers, newUserProject]);
      
      // Call the API to save this new user assignment
      saveUserProjectToAPI(newUserProject);
    }
  };

  
    const isAssigned = (user: User) =>
      !!selectedUsers.find((up) => up.identificationNum === user.identificationNum);

    /** Columnas filtrables. El id no se usa como campo, sino como clave del filtro. */
    const columnsDefinition: { id: string; label: string; value: (user: User) => string }[] = [
      { id: 'identificationNum', label: 'Rut', value: u => u.identificationNum || '' },
      { id: 'name', label: 'Nombre', value: u => u.name || '' },
      { id: 'firstName', label: 'Apellido Paterno', value: u => u.firstName || '' },
      { id: 'position', label: 'Cargo', value: u => u.position || 'Sin Cargo' },
      { id: 'profile', label: 'Perfil', value: u => PROFILE_NAMES[u.profile ?? 0] || 'Unknown' },
      { id: 'email', label: 'Email', value: u => u.email || '' },
      { id: 'assigned', label: 'Asignado', value: u => (isAssigned(u) ? 'Sí' : 'No') },
    ];

    const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>({});
    const [filterMenu, setFilterMenu] = useState<{ anchorEl: null | HTMLElement; columnKey: string | null }>({
      anchorEl: null,
      columnKey: null,
    });

    // Búsqueda global: incluye RUT, cargo y correo, no solo el nombre. Antes un usuario
    // sin nombre quedaba oculto incluso con el buscador vacío.
    const search = searchTerm.trim().toLowerCase();
    const filteredUsers = users.filter((user) => {
      if (search) {
        const haystack = [
          user.identificationNum,
          [user.name, user.firstName, user.lastName].filter(Boolean).join(' '),
          user.position,
          user.email,
        ];
        if (!haystack.some(value => value?.toLowerCase().includes(search))) return false;
      }

      return columnsDefinition.every(col => {
        const selected = columnFilters[col.id];
        return !selected || selected.length === 0 || selected.includes(col.value(user));
      });
    });

    const uniqueValues = useMemo<string[]>(() => {
      const column = columnsDefinition.find(col => col.id === filterMenu.columnKey);
      if (!column) return [];
      return Array.from(new Set(users.map(column.value).filter(value => !!value))).sort();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [users, selectedUsers, filterMenu.columnKey]);

    const handleColumnFilterChange = (selected: string[]) => {
      if (!filterMenu.columnKey) return;
      setColumnFilters(prev => ({ ...prev, [filterMenu.columnKey as string]: selected }));
    };

    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Asignar Usuarios al proyecto: {project.name}
        </Typography>
  
        <Box mb={2}>
          <TextField
            label="Buscar por RUT, nombre, cargo o correo"
            variant="outlined"
            fullWidth
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {columnsDefinition.map((col) => (
                  <TableCell key={col.id} sx={{ color: '#004F86', fontWeight: 'bold', verticalAlign: 'top' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {col.label}
                      <IconButton
                        size="small"
                        title={`Filtrar por ${col.label}`}
                        onClick={(e) => setFilterMenu({ anchorEl: e.currentTarget, columnKey: col.id })}
                        sx={{ color: (columnFilters[col.id]?.length ?? 0) > 0 ? '#004F86' : 'inherit' }}
                      >
                        <FilterListIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow key={user.identificationNum}>
                  <TableCell>{user.identificationNum}</TableCell>
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.firstName}</TableCell>
                  <TableCell>{user.position}</TableCell>
                  <TableCell>{PROFILE_NAMES[user.profile ?? 0] || 'Unknown'}</TableCell> {/* Displaying Profile Name */}              
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Checkbox
                      checked={!!selectedUsers.find((up) => up.identificationNum === user.identificationNum)}
                      onChange={() => handleCheckboxChange(user)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <ColumnFilterMenu
          anchorEl={filterMenu.anchorEl}
          values={uniqueValues}
          selected={filterMenu.columnKey ? (columnFilters[filterMenu.columnKey] || []) : []}
          onClose={() => setFilterMenu({ anchorEl: null, columnKey: null })}
          onChange={handleColumnFilterChange}
        />
      </Box>
    );
  };
  
  export default UserAssignmentTable;
  
  // Add this to avoid the --isolatedModules error
  export {};